import { absenceGrundLabel } from '../../../lib/v1';
import type { V1Absence } from '../../../lib/v1';
import i18n from '../../../i18n';
import { formatDate } from '../../../lib/dateFormat';
import { bkAvailabilityByGrund } from '../../../lib/bkAvailability';
import type { CareRow, TimelineBar, AbsenceTimelineBar, TimelineTask, TimelineVerordnung, TaskMarker } from './types';

const STATUS_KEY: Record<number, string> = {
  0: 'pflege.cares.status.new',
  1: 'pflege.cares.status.cancelled',
  2: 'pflege.cares.status.confirmed',
  3: 'pflege.cares.status.running',
  4: 'pflege.cares.status.completed',
};

export function statusLabel(
  status: number,
  translate: (key: string, opts?: Record<string, unknown>) => string,
): string {
  return STATUS_KEY[status]
    ? translate(STATUS_KEY[status])
    : translate('pflege.cares.status.generic', { n: status });
}

export function parseDate(v: unknown): Date | null {
  if (!v) return null;
  const d = new Date(String(v));
  return Number.isNaN(d.getTime()) ? null : startOfDay(d);
}

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function dayOfYear(d: Date): number {
  const start = new Date(d.getFullYear(), 0, 0);
  return Math.floor((startOfDay(d).getTime() - start.getTime()) / 86400000);
}

export function daysInYear(year: number): number {
  return new Date(year, 1, 29).getMonth() === 1 ? 366 : 365;
}

export function diffDays(a: Date, b: Date): number {
  return Math.max(0, Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / 86400000));
}

export function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

/** Locale-aware short month labels (Jan–Dez / Jan–Dec / sty–gru) for the timeline grid header. */
export function getMonthAbbreviations(lng: string): string[] {
  const fmt = new Intl.DateTimeFormat(lng || 'de', { month: 'short' });
  return Array.from({ length: 12 }, (_, i) => fmt.format(new Date(2021, i, 1)));
}

export function buildYearOptions(rows: CareRow[], selected: number): number[] {
  const current = new Date().getFullYear();
  const years = new Set<number>([selected, current, current + 1, current - 1, current - 2, current - 3]);
  rows.forEach(c => {
    const s = parseDate(c.startdatum);
    const e = parseDate(c.enddatum);
    if (s) years.add(s.getFullYear());
    if (e) years.add(e.getFullYear());
  });
  return [...years].sort((a, b) => b - a);
}

/**
 * Betreuungs-Balken der Jahresübersicht.
 *
 * ALLE Betreuungen stehen in EINER Zeile (row 0) — aufeinanderfolgende
 * Betreuungen (mit oder ohne Unterbrechung, auch überlappend) bilden eine
 * Linie und werden nicht in weitere Lanes gestapelt. Krankenhaus-
 * Abwesenheiten stehen in der zweiten Zeile (s. buildAbsenceBars).
 */
export function buildTimeline(
  rows: CareRow[],
  userRole: number,
  year: number,
  tr: (key: string, opts?: Record<string, unknown>) => string,
): TimelineBar[] {
  const yearStart = new Date(year, 0, 1);
  const yearEnd = new Date(year, 11, 31);
  const sorted = [...rows]
    .filter(c => c.status !== 1)
    .sort((a, b) => {
      const da = parseDate(a.startdatum)?.getTime() ?? 0;
      const db = parseDate(b.startdatum)?.getTime() ?? 0;
      return da - db;
    });
  return sorted
    .map(c => {
      const start = parseDate(c.startdatum);
      const end = parseDate(c.enddatum) ?? yearEnd;
      if (!start || end < yearStart || start > yearEnd) return null;
      const startClamped = start < yearStart ? yearStart : start;
      const endClamped = end > yearEnd ? yearEnd : end;
      const row = 0; // Einzeilige Darstellung (s. o.)
      const totalDays = daysInYear(year);
      const left = clamp((dayOfYear(startClamped) - 1) / totalDays * 100, 0, 100);
      const width = clamp(((diffDays(startClamped, endClamped) + 1) / totalDays) * 100, 1, 100 - left);
      const label = userRole === 2
        ? (c.bk_name ?? `BK #${c.bk_user_id}`)
        : (c.pb_name ?? `PB #${c.pb_user_id}`);
      return { care: c, left, width, row, label, statusLabel: statusLabel(c.status, tr) };
    })
    .filter((b): b is TimelineBar => Boolean(b));
}

export function summarizeCares(rows: CareRow[]) {
  const now = startOfDay(new Date()).getTime();
  return rows.reduce(
    (acc, c) => {
      const start = parseDate(c.startdatum)?.getTime() ?? 0;
      const end = parseDate(c.enddatum)?.getTime() ?? start;
      if (c.status === 1 || end < now) acc.pastOrCancelled += 1;
      else if (start <= now && now <= end) acc.current += 1;
      else if (start > now) acc.upcoming += 1;
      return acc;
    },
    { current: 0, upcoming: 0, pastOrCancelled: 0 },
  );
}

/** Vertragspause (alt: „Pause") — Unterbrechung der Betreuungslinie. */
export function isPauseGrund(grund: string | null | undefined): boolean {
  return ['Pause', 'Vertragspause'].includes((grund ?? '').trim());
}

/**
 * Abwesenheits-Balken. Die Jahresübersicht hat höchstens ZWEI Zeilen:
 *  - `pauseRow` (im CareYearTimeline 0): Vertragspausen liegen als
 *    Unterbrechung halbtransparent in der Zeile der Betreuungen,
 *  - `rowOffset` (im CareYearTimeline 1): alle übrigen Abwesenheiten
 *    (Krankenhaus) stehen gemeinsam in der zweiten Zeile.
 * Ohne `pauseRow` landen alle Abwesenheiten in `rowOffset`.
 */
export function buildAbsenceBars(
  absences: V1Absence[],
  year: number,
  rowOffset: number,
  absenceLabel: string,
  t: (k: string) => string,
  pauseRow: number = rowOffset,
): AbsenceTimelineBar[] {
  const yearStart = new Date(year, 0, 1);
  const yearEnd = new Date(year, 11, 31);
  const totalDays = daysInYear(year);
  const fmtD = (v: unknown) => {
    const d = parseDate(v);
    return d ? formatDate(d) : String(v ?? '—');
  };
  return absences
    .map(a => {
      const start = parseDate(a.startdatum);
      // Offenes Ende: BK-Verfügbarkeit („Verfügbar ab …") gilt bis auf Weiteres
      // → Balken bis zum Jahresende; sonst (PB-Abwesenheit) nur der Starttag.
      const end = parseDate(a.enddatum) ?? (bkAvailabilityByGrund(a.grund) ? (start && start > yearEnd ? start : yearEnd) : start);
      if (!start || !end || end < yearStart || start > yearEnd) return null;
      const startClamped = start < yearStart ? yearStart : start;
      const endClamped = end > yearEnd ? yearEnd : end;
      const left = clamp((dayOfYear(startClamped) - 1) / totalDays * 100, 0, 100);
      const width = clamp(((diffDays(startClamped, endClamped) + 1) / totalDays) * 100, 1, 100 - left);
      const grundLabel = absenceGrundLabel(a.grund, t) || absenceLabel;
      return {
        id: a.id,
        left, width,
        row: isPauseGrund(a.grund) ? pauseRow : rowOffset,
        grund: grundLabel,
        grundRaw: (a.grund ?? '').trim(),
        tooltip: `${absenceLabel}: ${grundLabel} · ${fmtD(a.startdatum)} – ${fmtD(a.enddatum)}`,
      };
    })
    .filter((b): b is AbsenceTimelineBar => Boolean(b));
}

/**
 * Batch-12 — Verordnungs-Termine als Diamant-Marker (analog Aufgaben).
 * Tooltip nennt Termin + ggf. Erinnerung ("X Tage vorher").
 */
export function buildVerordnungMarkers(
  verordnungen: TimelineVerordnung[],
  year: number,
  fmtDate: (v: unknown) => string,
  tr: (key: string, opts?: Record<string, unknown>) => string = (k, opts) => i18n.t(k, opts),
): TaskMarker[] {
  const totalDays = daysInYear(year);
  return verordnungen
    .map(v => {
      const termin = parseDate(v.termin);
      if (!termin || termin.getFullYear() !== year) return null;
      const left = clamp((dayOfYear(termin) / totalDays) * 100, 0, 100);
      const titel = (v.titel ?? '').toString().trim() || `#${v.id}`;
      const erinnerung = v.erinnerung_tage != null && v.erinnerung_tage > 0
        ? ` · ${tr('pflege.verordnungen.reminder_days', { count: v.erinnerung_tage })}`
        : '';
      return {
        id: v.id,
        left,
        label: titel,
        tooltip: `${titel} · ${fmtDate(v.termin)}${erinnerung}`,
        done: false,
      };
    })
    .filter((m): m is TaskMarker => Boolean(m));
}

export function buildTaskMarkers(
  tasks: TimelineTask[],
  year: number,
  fmtDateTime: (v: unknown) => string,
): TaskMarker[] {
  const totalDays = daysInYear(year);
  return tasks
    .map(task => {
      const due = parseDate(task.zieldatum);
      if (!due || due.getFullYear() !== year) return null;
      const left = clamp((dayOfYear(due) / totalDays) * 100, 0, 100);
      const titel = (task.titel ?? '').toString().trim() || `#${task.id}`;
      return {
        id: task.id,
        left,
        label: titel,
        tooltip: `${titel} · ${fmtDateTime(task.zieldatum)}`,
        done: task.status === 1,
      };
    })
    .filter((m): m is TaskMarker => Boolean(m));
}
